import { ref, computed, onMounted } from 'vue';
import type { AdditionalField } from './ListsTypes.d';
import type { ContextAccess } from '@shared/Root/Context.access';

export function useContactAdditionalFields(
    contextAccess: ContextAccess,
    options?: {
        hideAddedDateTime?: boolean;
        showingUnsubscribed?: boolean;
        showingBounceData?: boolean;
    }
) {
    const availableFields = computed<AdditionalField[]>(() => {
        const cf = contextAccess.state.currentAccount?.CustomField;
        const fields: AdditionalField[] = [
            { name: 'Email', displayName: $Resources.ContactEmailLabel, type: 'string', alwaysVisible: true },
            { name: 'SmsNumber', displayName: $Resources.ContactSmsNumberLabel, type: 'string', alwaysVisible: true },
            { name: 'FirstName', displayName: $Resources.ContactFirstNameLabel, type: 'string', alwaysVisible: true },
            { name: 'LastName', displayName: $Resources.ContactLastNameLabel, type: 'string', alwaysVisible: true },
            { name: 'JobTitle', displayName: $Resources.propertyContactJobTitleLabel, type: 'string' },
            { name: 'CompanyName', displayName: $Resources.propertyContactCompanyNameLabel, type: 'string' },
            { name: 'Birthday', displayName: $Resources.propertyContactBirthdayLabel, type: 'datetime' },
            { name: 'Gender', displayName: $Resources.propertyContactGenderLabel, type: 'string' },
            { name: 'Industry', displayName: $Resources.propertyContactIndustryLabel, type: 'string' },
            { name: 'Country', displayName: $Resources.propertyContactCountryLabel, type: 'string' },
            { name: 'State', displayName: $Resources.propertyContactStateLabel, type: 'string' },
            { name: 'City', displayName: $Resources.propertyContactCityLabel, type: 'string' },
            { name: 'PostalCode', displayName: $Resources.propertyContactPostalCodeLabel, type: 'string' },
            { name: 'StreetAddress', displayName: $Resources.propertyContactStreetAddressLabel, type: 'string' },
            { name: 'Website', displayName: $Resources.propertyContactWebsiteLabel, type: 'string' },
            { name: 'LastNpsDate', displayName: $Resources.ContactModalLastNpsDate, type: 'datetime' },
            { name: 'LastNpsRating', displayName: $Resources.ContactModalLastNpsRating, type: 'number' },
            { name: 'LastCsatDate', displayName: $Resources.contactModalLastCsatDate, type: 'datetime' },
            { name: 'LastCsatRating', displayName: $Resources.contactModalLastCsatRating, type: 'number' },
            { name: 'Region', displayName: $Resources.propertyContactRegionLabel, type: 'string' },
            { name: 'Salutation', displayName: $Resources.propertyContactSalutationLabel, type: 'string' },
            { name: 'SourceName', displayName: $Resources.ContactSourceLabel, type: 'string' },
        ];

        if (options?.showingBounceData) {
            fields.push({
                name: 'BouncedEmailDate',
                displayName: $Resources.BouncedEmailDateLabel,
                listDisplayName: $Resources.BouncedEmailDateListLabel,
                type: 'datetime',
            });

            fields.push({
                name: 'BouncedPhoneDate',
                displayName: $Resources.BouncedPhoneDateLabel,
                listDisplayName: $Resources.BouncedPhoneDateListLabel,
                type: 'datetime',
            });

            fields.push({ name: 'BounceReason', displayName: $Resources.BounceReasonLabel, type: 'bouncereason' });
        }

        if (cf) {
            for (let i = 1; i <= 10; i++) {
                const label = (cf as any)[`CustomField${i}`];
                if (label) fields.push({ name: `Custom${i}`, displayName: label, type: 'string' });
            }
            for (let i = 1; i <= 6; i++) {
                const label = (cf as any)[`CustomDate${i}`];
                if (label) fields.push({ name: `CustomDate${i}`, displayName: label, type: 'datetime' });
            }
            for (let i = 1; i <= 6; i++) {
                const label = (cf as any)[`CustomBool${i}`];
                if (label) fields.push({ name: `CustomBool${i}`, displayName: label, type: 'boolean' });
            }
            for (let i = 1; i <= 2; i++) {
                const label = (cf as any)[`CustomLong${i}`];
                if (label) fields.push({ name: `CustomLong${i}`, displayName: label, type: 'string' });
            }
            for (let i = 1; i <= 6; i++) {
                const label = (cf as any)[`CustomNumeric${i}`];
                if (label) fields.push({ name: `CustomNumeric${i}`, displayName: label, type: 'number' });
            }
        }

        fields.push(
            { name: 'ExternalId', displayName: 'ExternalId', type: 'string' },
            { name: 'Created', displayName: $Resources.CreatedLabel, type: 'datetime' },
            { name: 'LastModified', displayName: $Resources.LastModifiedLabel, type: 'datetime' }
        );

        if (!options?.hideAddedDateTime) {
            fields.push({ name: 'AddedDateTime', displayName: $Resources.AddedToListLabel, type: 'datetime' });
        }

        if (options?.showingUnsubscribed) {
            fields.unshift({ name: 'UnsubscribeReasonValue', displayName: $Resources.IssueStatisticsUnsubscribeReasonLabel, type: 'unsubscribe-reason' });
        }

        return fields;
    });

    const selectedFieldNames = ref<Set<string>>(new Set());
    const selectedFields = computed(() => availableFields.value.filter((f) => selectedFieldNames.value.has(f.name)));

    function toggleField(field: AdditionalField) {
        const names = new Set(selectedFieldNames.value);
        if (names.has(field.name)) names.delete(field.name);
        else names.add(field.name);
        selectedFieldNames.value = names;
        saveFieldsToStorage();
    }

    function storageKey(): string {
        return 'additionalFieldsToShow' + (contextAccess.state.currentAccount?.AccountId ?? '');
    }

    function saveFieldsToStorage() {
        try {
            localStorage.setItem(storageKey(), JSON.stringify(selectedFields.value.map((f) => ({ name: f.name }))));
        } catch {
            /* ignore */
        }
    }

    function loadFieldsFromStorage() {
        try {
            const stored = localStorage.getItem(storageKey());
            if (!stored) {
                if (options?.showingUnsubscribed) {
                    selectedFieldNames.value = new Set(['UnsubscribeReasonValue']);
                }
                return;
            }
            const parsed: { name: string }[] = JSON.parse(stored);
            const validNames = new Set(availableFields.value.map((f) => f.name));
            selectedFieldNames.value = new Set(parsed.map((f) => f.name).filter((n) => validNames.has(n)));
        } catch {
            /* ignore */
        }
    }

    onMounted(() => loadFieldsFromStorage());

    return { availableFields, selectedFieldNames, selectedFields, toggleField };
}
